import { useEffect, useState } from 'react'
import { Navigate, Route, Routes } from 'react-router-dom'
import { LoginRoute } from '@/routes/login'
import { HomeRoute } from '@/routes/home'
import { ProducaoListRoute } from '@/routes/producao/list'
import { ProducaoSetupRoute } from '@/routes/producao/setup'
import { ProducaoSessionRoute } from '@/routes/producao/session'
import { ProducaoSuccessRoute } from '@/routes/producao/success'
import { CarreteiroIndexRoute } from '@/routes/carreteiro/index'
import { CarreteiroStep0Route } from '@/routes/carreteiro/step0'
import { CarreteiroStep1Route } from '@/routes/carreteiro/step1'
import { CarreteiroStep2Route } from '@/routes/carreteiro/step2'
import { CarreteiroSuccessRoute } from '@/routes/carreteiro/success'
import { useCarreteiroStore } from '@/features/carreteiro/carreteiroStore'
import { bootstrapAutoSave } from '@/features/carreteiro/draftCarreteiroRepo'
import { ParteDiariaIndexRoute } from '@/routes/parteDiaria/index'
import { ParteDiariaStep0Route } from '@/routes/parteDiaria/step0'
import { ParteDiariaStep1Route } from '@/routes/parteDiaria/step1'
import { ParteDiariaStep2Route } from '@/routes/parteDiaria/step2'
import { ParteDiariaSuccessRoute } from '@/routes/parteDiaria/success'
import { useParteDiariaStore } from '@/features/parteDiaria/parteDiariaStore'
import { bootstrapAutoSaveParteDiaria } from '@/features/parteDiaria/draftParteDiariaRepo'
import { bootstrapLiveUpdate } from '@/core/platform/liveUpdate'
import { FotosListRoute } from '@/routes/fotos/list'
import { FotosCaptureRoute } from '@/routes/fotos/capture'
import { InstallRoute } from '@/routes/install'
import { useIsAuthenticated, useTokenValid } from '@/core/auth/SessionStore'
import { ReauthBanner } from '@/features/auth/ReauthBanner'
import { useForegroundSyncListeners } from '@/core/sync/foregroundSync'
import { requestPersistentStorage } from '@/core/platform/persistStorage'
import { initPhotoStorage } from '@/core/storage'
import { initCameraProvider } from '@/core/camera'
import { ToastHost } from '@/features/common/Toast'
import { ToastReconnected } from '@/features/common/ToastReconnected'
import { IosInstallBanner } from '@/features/install/IosInstallBanner'
import { UpdateBanner } from '@/features/liveUpdate/UpdateBanner'
import { AdminLiveUpdateRoute } from '@/routes/admin/liveUpdate'

/**
 * Root da aplicação:
 *  - LoaderScreen com identidade InfraWork enquanto useSyncExternalStore resolve.
 *  - Token vencido NÃO desloga: ReauthBanner convida a entrar de novo (offline-first).
 *  - Foreground sync engine ativo enquanto autenticado.
 *  - SW auto-update — autoUpdate + skipWaiting/clientsClaim.
 *  - Routing com guard de auth no nível das rotas (sem flash de login pós-login).
 */

function useReady(): boolean {
  const [ready, setReady] = useState(false)
  useEffect(() => {
    // Bootstrap providers conforme o runtime (web vs Capacitor native).
    // initPhotoStorage / initCameraProvider são no-op em web (já têm default).
    // Em native, fazem dynamic import dos plugins Capacitor.
    Promise.all([initPhotoStorage(), initCameraProvider()])
      .catch((err) => console.warn('[boot] provider init falhou:', err))
      .finally(() => setReady(true))
  }, [])
  return ready
}

/**
 * LoaderScreen — splash com identidade InfraWork. Usado em três momentos:
 *  - Boot inicial (antes do useSyncExternalStore resolver localStorage)
 *  - Carregamento de dados em rotas (passando `label` opcional)
 *  - Transições autenticadas (ex.: aguardando session refresh)
 *
 * Visual: icon centralizado com glow azul radial sutil de fundo, wordmark logo
 * abaixo, spinner discreto. Mesma vibe da Tela de Início standalone do PWA.
 */
export function LoaderScreen({ label }: { label?: string } = {}) {
  return (
    <div className="min-h-svh flex flex-col items-center justify-center bg-bg-base relative overflow-hidden">
      {/* Glow radial sutil — ecoa o efeito do icon-source.png */}
      <div
        aria-hidden
        className="absolute inset-0 pointer-events-none opacity-60"
        style={{
          background:
            'radial-gradient(circle at center, rgba(77,142,255,0.18) 0%, transparent 55%)',
        }}
      />
      <div className="relative flex flex-col items-center gap-8 px-8">
        {/* Wordmark com glow — peça única (icon + texto INFRAWORK).
            Largura responsiva, max-w pra não estourar em tablet. */}
        <img
          src="/logo-wordmark-glow.png"
          alt="InfraWork"
          className="w-full max-w-[320px] h-auto"
          decoding="async"
        />
        <div className="flex items-center gap-2 text-on-surface-dim text-xs">
          <div className="h-3 w-3 rounded-full border border-accent border-t-transparent animate-spin" />
          <span>{label ?? 'Carregando…'}</span>
        </div>
      </div>
    </div>
  )
}

function Splash() {
  return <LoaderScreen />
}

export function App() {
  const ready = useReady()
  const authed = useIsAuthenticated()
  const tokenValid = useTokenValid()

  // Foreground sync ativo enquanto autenticado (Chrome+iOS).
  // No Chrome também temos Background Sync via SW; iOS depende disso aqui.
  useForegroundSyncListeners(authed)

  // Auto-save de drafts de carreteiro: assina o store uma vez (idempotente)
  // e persiste em Dexie cada vez que muda. Permite multi-ticket e recuperação
  // após reload do PWA.
  useEffect(() => {
    bootstrapAutoSave(useCarreteiroStore)
    bootstrapAutoSaveParteDiaria(useParteDiariaStore)
  }, [])

  // Live Update (Capacitor APK): notifica plugin que o bundle JS carregou OK.
  // Se não chamarmos, plugin reverte pro bundle nativo após 10s (timeout).
  // No-op em PWA web.
  useEffect(() => {
    void bootstrapLiveUpdate()
  }, [])

  // Storage persistente — defende contra eviction do IndexedDB (perda de fotos
  // pendentes em memory pressure). Chrome Android aceita sem prompt quando PWA
  // está instalado; iOS Safari faz best-effort. Idempotente. Roda uma vez ao
  // estar autenticado.
  useEffect(() => {
    if (!authed) return
    void requestPersistentStorage()
  }, [authed])

  if (!ready) return <Splash />


  return (
    <>
      <UpdateBanner />
      <ReauthBanner />
      {authed && <ToastReconnected />}
      {authed && <IosInstallBanner />}
      <ToastHost />
      <Routes>
        {/* /login: se já autenticado, redireciona pra home no nível de rota.
            Sem isso, há um frame de flicker entre sessionStore.set e useAuth.navigate. */}
        <Route
          path="/login"
          element={authed && tokenValid ? <Navigate to="/" replace /> : <LoginRoute />}
        />
        <Route path="/" element={authed ? <HomeRoute /> : <Navigate to="/login" replace />} />
        <Route path="/producao" element={<ProducaoListRoute />} />
        <Route path="/producao/setup" element={<ProducaoSetupRoute />} />
        <Route path="/producao/session" element={<ProducaoSessionRoute />} />
        <Route path="/producao/success" element={<ProducaoSuccessRoute />} />
        <Route path="/carreteiro" element={<CarreteiroIndexRoute />} />
        <Route path="/carreteiro/step0" element={<CarreteiroStep0Route />} />
        <Route path="/carreteiro/step1" element={<CarreteiroStep1Route />} />
        <Route path="/carreteiro/step2" element={<CarreteiroStep2Route />} />
        <Route path="/carreteiro/success" element={<CarreteiroSuccessRoute />} />
        <Route path="/parte-diaria" element={<ParteDiariaIndexRoute />} />
        <Route path="/parte-diaria/step0" element={<ParteDiariaStep0Route />} />
        <Route path="/parte-diaria/step1" element={<ParteDiariaStep1Route />} />
        <Route path="/parte-diaria/step2" element={<ParteDiariaStep2Route />} />
        <Route path="/parte-diaria/success" element={<ParteDiariaSuccessRoute />} />
        <Route path="/fotos" element={<FotosListRoute />} />
        <Route path="/fotos/capture" element={<FotosCaptureRoute />} />
        <Route path="/install" element={<InstallRoute />} />
        <Route path="/admin/live-update" element={<AdminLiveUpdateRoute />} />
        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
    </>
  )
}
